import React, { useCallback } from 'react';

import { getWorkflowAiLifecycleState } from '@aline/common';
import type { WorkflowAiLifecycleTimestamps } from '@aline/common';
import { useSnackbar } from 'notistack';
import { AlineButton } from 'src/components/generic';
import { AlineAlert } from 'src/components/generic/AlineAlert';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  alert: {
    '& .MuiAlert-action': {
      alignItems: 'center',
      flexWrap: 'wrap',
      marginRight: 0,
    },
    '& .MuiAlert-icon': {
      alignItems: 'center',
      paddingTop: 0,
    },
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: theme.spacing(1.25, 1.5),
    transition: 'background-color 180ms ease, border-color 180ms ease',
  },
  icon: {
    alignItems: 'center',
    paddingBottom: 0,
    paddingTop: 0,
  },
}));

interface WorkflowAiBuildAlertProps {
  workflow: WorkflowAiLifecycleTimestamps & { id: string; aiFailureReason?: string | null };
}

export function WorkflowAiBuildAlert({ workflow }: WorkflowAiBuildAlertProps) {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [cancelWorkflowAiBuild, { loading: cancelling }] =
    Apollo.useCancelWorkflowAiBuildMutation();
  const state = getWorkflowAiLifecycleState(workflow);
  const handleCancel = useCallback(async () => {
    try {
      await cancelWorkflowAiBuild({ variables: { workflowId: workflow.id } });
      enqueueSnackbar('Workflow build cancelled.', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to cancel workflow build.', {
        variant: 'error',
      });
    }
  }, [cancelWorkflowAiBuild, enqueueSnackbar, workflow.id]);

  switch (state) {
    case 'building':
      return (
        <AlineAlert
          action={
            <AlineButton
              color="primary"
              loading={cancelling}
              size="small"
              variant="text"
              onClick={handleCancel}
            >
              Stop
            </AlineButton>
          }
          className={classes.alert}
          classes={{ icon: classes.icon }}
          icon={<PulseIcon variant={PulseIconVariant.LoadingPrimary} />}
          severity="info"
        >
          Aline AI is building your workflow
        </AlineAlert>
      );
    case 'failed':
      return (
        <AlineAlert className={classes.alert} classes={{ icon: classes.icon }} severity="error">
          {workflow.aiFailureReason || 'Aline AI failed to build your workflow'}
        </AlineAlert>
      );
    case 'cancelled':
    case 'finished':
    case 'idle':
      return null;
  }
}
